import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n/useI18n';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { projectApi } from '../api/client';

export default function CreateProject() {
  const { t, language } = useI18n();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    performer_email: '',
    initial_points: '',
    type: '0',
  });

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await projectApi.create({
        title: formData.title,
        performer_email: formData.performer_email,
        initial_points: parseFloat(formData.initial_points) || 0,
        type: parseInt(formData.type),
        language: language,
      });

      if (res.success) {
        alert(t('projectCreated'));
        navigate('/');
      } else {
        // 显示后端返回的具体错误消息
        alert(t('createFailed') + (res.message || t('actionFailed')));
      }
    } catch (err) {
      // 检查错误响应是否包含具体的消息
      if (err.response) {
        // Axios错误响应已经在拦截器中处理为data格式
        const errorData = err.response.data;
        if (errorData && errorData.message) {
          alert(t('createFailed') + errorData.message);
        } else {
          alert(t('createFailed') + (errorData?.message || t('actionFailed')));
        }
      } else {
        // 网络错误或其他非HTTP错误
        alert(t('networkErrorCreate'));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b p-4 sticky top-0 z-50">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/')}
              className="mr-3 text-gray-400 p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
              </svg>
            </button>
            <h1 className="text-lg font-bold text-gray-800">{t('createProjectTitle')}</h1>
          </div>
          <LanguageSwitcher />
        </div>
      </nav>

      <main className="container mx-auto p-4 max-w-md">
        <form onSubmit={handleSubmit} className="space-y-6">
          <section className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 space-y-4">
            <h3 className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">{t('basicInfo')}</h3>

            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1">{t('projectTitle')}</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder={t('projectNamePlaceholder')}
                className="w-full bg-gray-50 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-xl p-3 outline-none transition-all font-bold text-gray-800"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1">{t('performerEmail')}</label>
              <input
                type="email"
                value={formData.performer_email}
                onChange={(e) => setFormData({ ...formData, performer_email: e.target.value })}
                required
                placeholder={t('emailPlaceholder')}
                className="w-full bg-gray-50 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-xl p-3 outline-none transition-all font-bold text-gray-800"
              />
              <p className="text-[9px] text-gray-400 mt-2 px-1">{t('performerEmailHint')}</p>
            </div>
          </section>

          <section className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 space-y-5">
            <h3 className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">{t('modeAndRules')}</h3>

            <div>
              <label className="block text-xs font-bold text-gray-400 mb-3">{t('selectMode')}</label>
              <div className="grid grid-cols-2 gap-3">
                <label>
                  <input
                    type="radio"
                    name="type"
                    value="0"
                    checked={formData.type === '0'}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="hidden peer"
                  />
                  <div className="mode-transition h-full p-4 border-2 border-gray-50 rounded-2xl bg-gray-50 text-gray-400 peer-checked:border-green-500 peer-checked:bg-green-50 peer-checked:text-green-600 transition-all text-center cursor-pointer">
                    <div className="text-2xl mb-1">📈</div>
                    <div className="font-bold text-sm">{t('positiveSystem')}</div>
                    <div className="text-[9px] mt-1 opacity-70">{t('positiveSystemDesc')}</div>
                  </div>
                </label>
                <label>
                  <input
                    type="radio"
                    name="type"
                    value="1"
                    checked={formData.type === '1'}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="hidden peer"
                  />
                  <div className="mode-transition h-full p-4 border-2 border-gray-50 rounded-2xl bg-gray-50 text-gray-400 peer-checked:border-red-500 peer-checked:bg-red-50 peer-checked:text-red-600 transition-all text-center cursor-pointer">
                    <div className="text-2xl mb-1">📉</div>
                    <div className="font-bold text-sm">{t('negativeSystem')}</div>
                    <div className="text-[9px] mt-1 opacity-70">{t('negativeSystemDesc')}</div>
                  </div>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1">{t('initialPoints')}</label>
              <div className="relative">
                <input
                  type="number"
                  value={formData.initial_points}
                  onChange={(e) => setFormData({ ...formData, initial_points: e.target.value })}
                  required
                  placeholder="0"
                  className="w-full bg-gray-50 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-xl p-3 pl-10 outline-none transition-all font-mono font-bold"
                />
                <span className="absolute left-4 top-3.5 text-gray-300">#</span>
              </div>
              <p className="text-[9px] text-gray-400 mt-2 italic px-1">{t('initialPointsHint')}</p>
            </div>
          </section>

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold shadow-lg shadow-indigo-100 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('creating') : t('confirmCreate')}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
